import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { toast } from '@/components/ui/use-toast';

// Types
interface Platform {
  id: string;
  name: string;
  platform_key: string;
  type: 'SOCIAL' | 'BLOG';
  metadata: Record<string, any> | null;
  created_at: string;
  updated_at: string;
  enabled: boolean;
  description?: string;
}

interface PlatformBrandSetting {
  id: string;
  brand_id: string;
  platform_id: string;
  autopost_enabled: boolean;
  autogenerate_enabled?: boolean;
  posting_frequency: string;
  preferred_time: string;
  preferred_days: string[] | null;
  metadata: Record<string, any> | null;
  config?: {
    image?: {
      enabled: boolean;
      steps: string[];
    };
    video?: {
      enabled: boolean;
      steps: string[];
    };
  };
  created_at: string;
  updated_at: string;
  status: string;
  auth_token?: string | null;
  auth_refresh_token?: string | null;
  auth_expires_at?: string | null;
  connection_details: Record<string, any> | null;
  error_message?: string | null;
  user_id?: string;
}

interface PlatformSettings {
  platform_id: string;
  autopost_enabled: boolean;
  autogenerate_enabled: boolean;
  posting_frequency: 'HOURLY' | 'DAILY' | 'WEEKLY' | 'BIWEEKLY' | 'MONTHLY';
  preferred_time: string;
  preferred_days?: string[];
  config?: {
    image?: {
      enabled: boolean;
      steps: string[];
    };
    video?: {
      enabled: boolean;
      steps: string[];
    };
  };
  status: 'CONNECTED' | 'DISCONNECTED' | 'AUTHENTICATION_FAILED' | 'ERROR' | 'PENDING';
  connection_details?: Record<string, any>;
  error_message?: string;
}

// Define the LinkedIn callback event interface
interface LinkedInCallbackEvent {
  type: string;
  code?: string;
  state?: string;
  error?: string;
  errorDescription?: string;
}

// New interface for LinkedIn organizations
interface LinkedInOrganization {
  id: string;
  name: string;
  vanityName?: string;
  logoUrl?: string;
}

// Define the Twitter callback event interface
interface TwitterCallbackEvent {
  type: string;
  code?: string;
  state?: string;
  error?: string;
  errorDescription?: string;
}

// Define the Instagram callback event interface
interface InstagramCallbackEvent {
  type: string;
  code?: string;
  state?: string;
  error?: string;
  errorDescription?: string;
}

// Define the Facebook callback event interface
interface FacebookCallbackEvent {
  type: string;
  code?: string;
  state?: string;
  error?: string;
  errorDescription?: string;
}

export const usePlatformSettings = (brandId: string | undefined) => {
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [platformSettings, setPlatformSettings] = useState<Record<string, PlatformBrandSetting>>({});
  const [platformConnectionStatus, setPlatformConnectionStatus] = useState<Record<string, 'idle' | 'success' | 'error' | 'testing'>>({});
  const [loading, setLoading] = useState(true);
  // Add state for LinkedIn organizations
  const [linkedinOrganizations, setLinkedinOrganizations] = useState<LinkedInOrganization[]>([]);
  const [loadingOrganizations, setLoadingOrganizations] = useState(false);

  // Initialize platform connection status
  useEffect(() => {
    if (!brandId) return;

    const fetchPlatforms = async () => {
      try {
        const { data, error } = await supabase
          .from('platforms')
          .select('*');

        if (error) throw error;

        const platformsData = data as Platform[] || [];
        setPlatforms(platformsData);

        // Set initial connection status for all platforms
        const statuses: Record<string, 'idle' | 'success' | 'error' | 'testing'> = {};
        platformsData.forEach(platform => {
          statuses[platform.id] = 'idle';
        });
        setPlatformConnectionStatus(statuses);

        // Fetch platform SETTINGS for the brand
        await fetchPlatformSettings(platformsData);
      } catch (error) {
        console.error('Error fetching platforms:', error);
        toast({
          title: 'Error',
          description: 'Failed to load platform data',
          variant: 'destructive',
        });
      }
    };

    fetchPlatforms();
  }, [brandId]);

  // Helper function to check if OAuth token is expired
  const isTokenExpired = (connectionDetails: Record<string, any> | null): boolean => {
    if (!connectionDetails || !connectionDetails.expires_at) {
      return false; // Cannot determine expiration
    }

    try {
      const expiresAt = new Date(connectionDetails.expires_at).getTime();
      const now = Date.now();

      // Consider token expired if it has already expired
      return now >= expiresAt;
    } catch (error) {
      console.error('Error parsing expires_at:', error);
      return false;
    }
  };

  // Fetch platform settings
  const fetchPlatformSettings = async (platformsData: Platform[]) => {
    if (!brandId) return;

    try {
      const { data, error } = await supabase
        .from('platform_brand_settings')
        .select('*, platforms(name, platform_key, type)')
        .eq('brand_id', brandId);

      if (error) throw error;

      console.log('=== fetchPlatformSettings DATA FROM DB ===');
      console.log('Raw data from database:', JSON.stringify(data, null, 2));

      // Convert array to record for easier lookup
      const settingsMap: Record<string, PlatformBrandSetting> = {};
      const expiredPlatforms: Array<{ id: string; platformId: string }> = [];

      data?.forEach(setting => {
        // Extract error message from connection_details if it exists and no top-level error_message is set
        if (!setting.error_message && setting.connection_details) {
          const connectionDetails = setting.connection_details as Record<string, any>;
          if (connectionDetails.error_message) {
            setting.error_message = connectionDetails.error_message as string;
          }
        }

        // Check if token is expired and status is still CONNECTED
        if (setting.status === 'CONNECTED' && isTokenExpired(setting.connection_details as Record<string, any>)) {
          console.log(`Platform ${setting.platform_id} token has expired`);
          // Mark as expired and queue for update
          setting.status = 'AUTHENTICATION_FAILED';
          setting.error_message = 'OAuth token has expired. Please reconnect your account.';
          expiredPlatforms.push({ id: setting.id, platformId: setting.platform_id });
        }

        // Check if status is DISCONNECTED and we have an error message
        if (setting.status === 'DISCONNECTED' && setting.error_message) {
          console.log(`Platform ${setting.platform_id} is disconnected with error: ${setting.error_message}`);
        }

        settingsMap[setting.platform_id] = setting as PlatformBrandSetting;
      });

      // Update expired platforms in the database asynchronously
      if (expiredPlatforms.length > 0) {
        console.log(`Updating ${expiredPlatforms.length} expired platform(s) in database`);
        expiredPlatforms.forEach(async ({ id }) => {
          try {
            await supabase
              .from('platform_brand_settings')
              .update({
                status: 'AUTHENTICATION_FAILED',
                error_message: 'OAuth token has expired. Please reconnect your account.',
                updated_at: new Date().toISOString()
              })
              .eq('id', id);
          } catch (updateError) {
            console.error(`Failed to update expired platform ${id}:`, updateError);
          }
        });
      }

      setPlatformSettings(settingsMap);

      // Create default settings for platforms that don't have settings yet
      await createMissingPlatformSettings(platformsData, settingsMap);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching platform settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to load platform settings',
        variant: 'destructive',
      });
      setLoading(false);
    }
  };

  // Create missing platform settings
  const createMissingPlatformSettings = async (
    platforms: Platform[],
    existingSettings: Record<string, PlatformBrandSetting>
  ) => {
    if (!brandId || !platforms || platforms.length === 0) return;

    try {
      // Get the current user ID
      const session = await supabase.auth.getSession();
      const userId = session.data.session?.user.id;

      if (!userId) {
        console.warn('User ID not available, skipping default settings creation');
        return;
      }

      // Find platforms without settings
      const platformsWithoutSettings = platforms.filter(platform => !existingSettings[platform.id]);

      if (platformsWithoutSettings.length === 0) {
        // console.log('All platforms already have settings');
        return;
      }

      console.log(`Creating default settings for ${platformsWithoutSettings.length} platforms`);

      // Create default settings for each platform
      const defaultSettingsToCreate = platformsWithoutSettings.map(platform => ({
        brand_id: brandId,
        platform_id: platform.id,
        autopost_enabled: false,
        autogenerate_enabled: false,
        posting_frequency: 'DAILY',
        preferred_time: '09:00',
        preferred_days: [],
        // RSS doesn't need authentication, so mark it as CONNECTED automatically
        status: platform.platform_key === 'rss' ? 'CONNECTED' : 'DISCONNECTED',
        connection_details: {},
        user_id: userId
      }));

      // Batch insert all default settings
      const { data: newSettings, error } = await supabase
        .from('platform_brand_settings')
        .insert(defaultSettingsToCreate)
        .select();

      if (error) {
        console.error('Error creating default platform settings:', error);
        return;
      }

      console.log(`Created ${newSettings?.length || 0} default platform settings`);

      // Add the new settings to our state
      if (newSettings && newSettings.length > 0) {
        setPlatformSettings(prev => {
          const newState = { ...prev };
          newSettings.forEach(setting => {
            newState[setting.platform_id] = setting as PlatformBrandSetting;
          });
          return newState;
        });
      }

    } catch (error) {
      console.error('Error creating default platform settings:', error);
    }
  };

  // Save platform settings
  const savePlatformSettings = async (platformId: string, settings: Partial<PlatformSettings>) => {
    if (!brandId) return;

    try {
      // Get user info for the user_id field
      const session = await supabase.auth.getSession();
      const userId = session.data.session?.user.id;

      if (!userId) {
        throw new Error('User ID not available. Please log in again.');
      }

      // Get updated brand settings from the DB
      const { data: existingSettings, error: existingSettingsError } = await supabase
        .from('platform_brand_settings')
        .select('*')
        .eq('platform_id', platformId)
        .eq('brand_id', brandId)
        .single();

      if (existingSettingsError) {
        throw new Error('Failed to fetch brand settings');
      }

      if (existingSettings) {
        // Update existing settings
        const { data, error } = await supabase
          .from('platform_brand_settings')
          .update({
            autopost_enabled: settings.autopost_enabled !== undefined ? settings.autopost_enabled : existingSettings.autopost_enabled,
            autogenerate_enabled: settings.autogenerate_enabled !== undefined ? settings.autogenerate_enabled : existingSettings.autogenerate_enabled || false,
            posting_frequency: settings.posting_frequency || existingSettings.posting_frequency,
            preferred_time: settings.preferred_time || existingSettings.preferred_time,
            preferred_days: settings.preferred_days || existingSettings.preferred_days,
            config: settings.config !== undefined ? settings.config : existingSettings.config,
            updated_at: new Date().toISOString(),
            connection_details: settings.connection_details || existingSettings.connection_details,
            status: settings.status || existingSettings.status,
            error_message: settings.error_message || existingSettings.error_message
          })
          .eq('id', existingSettings.id)
          .select()
          .single();

        if (error) throw error;

        // Update local state
        setPlatformSettings(prev => {
          const newState = { ...prev };
          newState[platformId] = data as PlatformBrandSetting;
          return newState;
        });
      } else {
        // Create new settings with uppercase posting_frequency
        const { data, error } = await supabase
          .from('platform_brand_settings')
          .insert({
            brand_id: brandId,
            platform_id: platformId,
            autopost_enabled: settings.autopost_enabled || false,
            autogenerate_enabled: settings.autogenerate_enabled || false,
            posting_frequency: settings.posting_frequency || 'WEEKLY',
            preferred_time: settings.preferred_time || '09:00',
            preferred_days: settings.preferred_days || [],
            config: settings.config || null,
            status: settings.status || 'DISCONNECTED',
            connection_details: settings.connection_details || {},
            user_id: userId,
            error_message: settings.error_message || null,
          })
          .select()
          .single();

        if (error) throw error;

        // Update local state
        setPlatformSettings(prev => {
          const newState = { ...prev };
          newState[platformId] = data as PlatformBrandSetting;
          return newState;
        });
      }

      toast({
        title: 'Settings Saved',
        description: 'Platform settings have been updated successfully',
        variant: 'default',
        duration: 3000,
        className: 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-900/50',
      });

    } catch (error) {
      console.error('Error saving platform settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to save platform settings',
        variant: 'destructive',
      });
    }
  };

  // Update connection status
  const updateConnectionStatus = async (
    platformId: string,
    status: 'CONNECTED' | 'DISCONNECTED' | 'AUTHENTICATION_FAILED' | 'PENDING',
    errorMessage?: string
  ) => {
    if (!brandId) return;

    try {
      // Make sure we're using a valid status value that matches DB constraints
      let validStatus = status;
      // Check if the status is one of the allowed values - if not, default to DISCONNECTED
      if (!['CONNECTED', 'DISCONNECTED', 'AUTHENTICATION_FAILED', 'PENDING'].includes(validStatus)) {
        console.warn(`Invalid status value: ${status}, defaulting to DISCONNECTED`);
        validStatus = 'DISCONNECTED';
      }

      // Don't update the database unnecessarily if the status hasn't changed
      const currentSettings = platformSettings[platformId];
      if (currentSettings && currentSettings.status === validStatus) {
        console.log(`Status already set to ${validStatus}, skipping update`);

        // Still update the local state to ensure UI is consistent
        setPlatformConnectionStatus(prev => ({
          ...prev,
          [platformId]: validStatus === 'CONNECTED' ? 'success' :
            validStatus === 'DISCONNECTED' ? 'idle' :
              validStatus === 'AUTHENTICATION_FAILED' ? 'error' : 'testing'
        }));

        return;
      }

      // Update the database
      const updateData: Partial<PlatformBrandSetting> = {
        status: validStatus,
        updated_at: new Date().toISOString()
      };

      // Add error message if provided
      if (errorMessage) {
        updateData.error_message = errorMessage;

        // Also update the connection_details to include the error message
        const { data: currentSettings } = await supabase
          .from('platform_brand_settings')
          .select('connection_details')
          .eq('platform_id', platformId)
          .eq('brand_id', brandId)
          .single();

        if (currentSettings && currentSettings.connection_details) {
          updateData.connection_details = {
            ...(currentSettings.connection_details as Record<string, any>),
            error_message: errorMessage
          };
        } else {
          updateData.connection_details = { error_message: errorMessage };
        }
      } else {
        // Clear error message if status is no longer an error
        updateData.error_message = null;

        // Update connection_details to clear error message too
        const { data: currentSettings } = await supabase
          .from('platform_brand_settings')
          .select('connection_details')
          .eq('platform_id', platformId)
          .eq('brand_id', brandId)
          .single();

        if (currentSettings && currentSettings.connection_details) {
          const updatedConnectionDetails = { ...(currentSettings.connection_details as Record<string, any>) };
          delete updatedConnectionDetails.error_message;
          updateData.connection_details = updatedConnectionDetails;
        }
      }

      // Wrap the update in a try-catch to handle potential constraint errors gracefully
      try {
        const { error } = await supabase
          .from('platform_brand_settings')
          .update(updateData)
          .eq('platform_id', platformId)
          .eq('brand_id', brandId);

        if (error) {
          console.error('Database error updating connection status:', error);
          // Don't throw here, just log it - we still want to update the local state
        }
      } catch (dbError) {
        console.error('Exception during database update:', dbError);
        // Don't rethrow, we'll still update the UI state
      }

      // Always update local state regardless of DB success
      setPlatformSettings(prev => {
        const newState = { ...prev };
        if (newState[platformId]) {
          newState[platformId] = {
            ...newState[platformId],
            status: validStatus,
            ...(errorMessage ? { error_message: errorMessage } : {})
          };
        }
        return newState;
      });

      // Update connection status icon
      setPlatformConnectionStatus(prev => ({
        ...prev,
        [platformId]: validStatus === 'CONNECTED' ? 'success' :
          validStatus === 'DISCONNECTED' ? 'idle' :
            validStatus === 'AUTHENTICATION_FAILED' ? 'error' :
              validStatus === 'PENDING' ? 'testing' : 'testing'
      }));

      // Only show toast for errors or when manually disconnecting
      if (validStatus !== 'CONNECTED' || errorMessage) {
        // Special handling for PENDING status to make it less alarming
        if (validStatus === 'PENDING') {
          toast({
            title: 'Connecting...',
            description: 'Setting up your platform connection. This may take a moment.',
            variant: 'default',
            className: 'bg-blue-50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-900/50',
          });
        } else {
          toast({
            title: `Connection ${validStatus === 'DISCONNECTED' ? 'Disabled' : validStatus}`,
            description: errorMessage || `Platform status has been set to ${validStatus}`,
            variant: validStatus === 'CONNECTED' ? 'default' : 'destructive',
            className: validStatus === 'CONNECTED' ? 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-900/50' : '',
          });
        }
      }
    } catch (error) {
      console.error('Error updating connection status:', error);
      toast({
        title: 'Error',
        description: 'Failed to update connection status',
        variant: 'destructive',
      });
    }
  };

  // Initiate the authentication flow for a platform
  const initiateAuthFlow = async (platformKey: string, authType?: 'FACEBOOK_LOGIN' | 'INSTAGRAM_LOGIN') => {
    if (!brandId) return;

    try {
      // Get the current user ID
      const session = await supabase.auth.getSession();
      const userId = session.data.session?.user.id;
      const accessToken = session.data.session?.access_token;

      if (!userId) {
        throw new Error('User ID not available. Please log in again.');
      }

      // Find the platform by key
      const platform = platforms.find(p => p.platform_key === platformKey);

      if (!platform) {
        throw new Error(`Platform ${platformKey} not found`);
      }

      // Set status to pending
      await updateConnectionStatus(platform.id, 'PENDING');

      // Special handling for LinkedIn
      if (platformKey === 'linkedin') {
        await initiateLinkedInAuth(platform);
        return;
      }

      // Special handling for Twitter
      if (platformKey === 'twitter') {
        await initiateTwitterAuth(platform);
        return;
      }

      // Special handling for Instagram
      if (platformKey === 'instagram') {
        await initiateInstagramAuth(platform, authType);
        return;
      }

      // Special handling for Facebook
      if (platformKey === 'facebook') {
        await initiateFacebookAuth(platform);
        return;
      }

      // For RSS, it doesn't need authentication - just mark as connected
      if (platformKey === 'rss') {
        await updateConnectionStatus(platform.id, 'CONNECTED');

        // Show success toast
        toast({
          title: 'RSS Feed Ready',
          description: `Your RSS feed is now available for ${platform.name}`,
          variant: 'default',
          className: 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-900/50',
        });

        // Set idle status after a short delay
        setTimeout(() => {
          setPlatformConnectionStatus(prev => ({ ...prev, [platform.id]: 'idle' }));
        }, 3000);

        return;
      }

      // For platforms that don't need actual authentication, we can directly set them as connected
      if (platformKey === 'wordpress' || platformKey === 'medium') {
        await updateConnectionStatus(platform.id, 'CONNECTED');

        // Show success toast
        toast({
          title: 'Connection Successful',
          description: `Successfully connected to ${platform.name}`,
          variant: 'default',
          className: 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-900/50',
        });

        // Set idle status after a short delay
        setTimeout(() => {
          setPlatformConnectionStatus(prev => ({ ...prev, [platform.id]: 'idle' }));
        }, 3000);

        return;
      }

      // Default handling for other platforms
      const response = await fetch(`${import.meta.env.VITE_WORKERS_URL}/rainfactory/test-connection/${platformKey}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          brandId: brandId,
          platformId: platform.id
        }),
      });

      const result = await response.json();

      if (result.success) {
        setPlatformConnectionStatus(prev => ({ ...prev, [platform.id]: 'success' }));
        toast({
          title: 'Connection Successful',
          description: result.message || `Successfully connected to ${platform.name}`,
          variant: 'default',
          className: 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-900/50',
        });

        // Update platform status in DB
        await updateConnectionStatus(platform.id, 'CONNECTED');
      } else {
        // Update both database and UI state on error
        setPlatformConnectionStatus(prev => ({ ...prev, [platform.id]: 'error' }));
        await updateConnectionStatus(platform.id, 'AUTHENTICATION_FAILED', result.message);

        toast({
          title: 'Connection Failed',
          description: result.message || `Could not connect to ${platform.name}`,
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error initiating auth flow:', error);

      // Find the platform to update its status
      const platform = platforms.find(p => p.platform_key === platformKey);
      if (platform) {
        // Update both database and UI state on error
        setPlatformConnectionStatus(prev => ({ ...prev, [platform.id]: 'error' }));
        await updateConnectionStatus(platform.id, 'AUTHENTICATION_FAILED', error instanceof Error ? error.message : 'Failed to start authentication process');
      }

      toast({
        title: 'Authentication Error',
        description: error instanceof Error ? error.message : 'Failed to start authentication process',
        variant: 'destructive',
      });
    }
  };

  // Handle LinkedIn OAuth flow initialization
  const initiateLinkedInAuth = async (platform: Platform) => {
    try {
      const session = await supabase.auth.getSession();
      const accessToken = session.data.session?.access_token;

      // Define the redirect URI - must be registered with LinkedIn
      const redirectUri = `${window.location.origin}/auth/linkedin/callback`;

      // Call our worker to initiate the OAuth flow
      const response = await fetch(`${import.meta.env.VITE_WORKERS_URL}/rainfactory/connections/initiate-auth/linkedin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          brandId,
          platformId: platform.id,
          redirectUri
        }),
      });

      const result = await response.json();

      if (!result.success || !result.authUrl) {
        throw new Error(result.message || 'Failed to initiate LinkedIn authentication');
      }

      // Store state and platform info in localStorage for the callback
      localStorage.setItem('linkedin_auth_state', result.state);
      localStorage.setItem('linkedin_auth_platform_id', platform.id);
      localStorage.setItem('linkedin_auth_brand_id', brandId || '');

      // Open the LinkedIn authorization page in a new window
      window.open(result.authUrl, 'linkedin_auth', 'width=600,height=700');

      // Listen for message from the popup window when auth is complete
      window.addEventListener('message', handleLinkedInCallback, false);

      toast({
        title: 'LinkedIn Authentication',
        description: 'Please complete the authentication in the popup window',
        duration: 10000,
      });

    } catch (error) {
      console.error('Error initiating LinkedIn auth:', error);
      setPlatformConnectionStatus(prev => ({ ...prev, [platform.id]: 'error' }));
      toast({
        title: 'LinkedIn Authentication Failed',
        description: error instanceof Error ? error.message : 'Failed to start LinkedIn authentication',
        variant: 'destructive',
      });

      setTimeout(() => {
        setPlatformConnectionStatus(prev => ({ ...prev, [platform.id]: 'idle' }));
      }, 3000);
    }
  };

  // Handle the callback from LinkedIn OAuth
  const handleLinkedInCallback = async (event: MessageEvent) => {
    // Make sure we only handle messages from our domain
    if (event.origin !== window.location.origin) return;

    // Check if this is our LinkedIn callback message
    const data = event.data as LinkedInCallbackEvent;
    if (data.type !== 'linkedin_auth_callback') return;

    try {
      // Remove the message listener
      window.removeEventListener('message', handleLinkedInCallback);

      const { code, state, error } = data;

      // Retrieve state and platform info from localStorage
      const storedState = localStorage.getItem('linkedin_auth_state');
      const platformId = localStorage.getItem('linkedin_auth_platform_id');
      const brandId = localStorage.getItem('linkedin_auth_brand_id');

      // Clean up localStorage
      localStorage.removeItem('linkedin_auth_state');
      localStorage.removeItem('linkedin_auth_platform_id');
      localStorage.removeItem('linkedin_auth_brand_id');

      // Verify state to prevent CSRF
      if (state !== storedState) {
        throw new Error('Invalid state parameter. Authentication request may have been tampered with.');
      }

      // Check for errors from the OAuth flow
      if (error) {
        if (error === 'user_cancelled_login' || error === 'user_cancelled_authorize') {
          throw new Error('Authentication was canceled by the user.');
        } else {
          throw new Error(`LinkedIn authentication error: ${error}`);
        }
      }

      // If we have a valid code, exchange it for an access token
      if (!code) {
        throw new Error('No authorization code received');
      }

      // Call our worker to exchange the code for a token
      const session = await supabase.auth.getSession();
      const accessToken = session.data.session?.access_token;

      const redirectUri = `${window.location.origin}/auth/linkedin/callback`;

      const response = await fetch(`${import.meta.env.VITE_WORKERS_URL}/rainfactory/connections/auth-callback/linkedin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          code,
          state,
          redirectUri,
          brandId,
          platformId
        }),
      });

      const result = await response.json();

      if (result.success) {
        // Successfully authenticated with LinkedIn
        setPlatformConnectionStatus(prev => ({ ...prev, [platformId!]: 'success' }));
        toast({
          title: 'LinkedIn Connected',
          description: 'Successfully connected to LinkedIn',
          variant: 'default',
          className: 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-900/50',
        });

        // Update connection status
        await updateConnectionStatus(platformId!, 'CONNECTED');

        // Refresh data to show the updated connection status
        await fetchPlatformSettings(platforms);
      } else {
        throw new Error(result.message || 'Failed to complete LinkedIn authentication');
      }

    } catch (error) {
      console.error('LinkedIn callback error:', error);
      toast({
        title: 'LinkedIn Connection Failed',
        description: error instanceof Error ? error.message : 'Failed to connect to LinkedIn',
        variant: 'destructive',
      });

      // Reset connection status for any LinkedIn platform
      const linkedinPlatform = platforms.find(p => p.platform_key === 'linkedin');
      if (linkedinPlatform) {
        setPlatformConnectionStatus(prev => ({ ...prev, [linkedinPlatform.id]: 'error' }));
        setTimeout(() => {
          setPlatformConnectionStatus(prev => ({ ...prev, [linkedinPlatform.id]: 'idle' }));
        }, 3000);
      }
    }
  };

  // Handle X (Twitter) OAuth flow initialization
  const initiateTwitterAuth = async (platform: Platform) => {
    try {
      // Get the current user ID
      const session = await supabase.auth.getSession();
      const userId = session.data.session?.user.id;
      const accessToken = session.data.session?.access_token;

      if (!userId) {
        throw new Error('User ID not available. Please log in again.');
      }

      // Define the redirect URI for Twitter OAuth
      const redirectUri = `${window.location.origin}/auth/twitter/callback`;

      // Call our worker to initiate the OAuth flow
      const response = await fetch(`${import.meta.env.VITE_WORKERS_URL}/rainfactory/connections/initiate-auth/twitter`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          brandId,
          platformId: platform.id,
          redirectUri
        }),
      });

      const result = await response.json();

      if (!result.success || !result.authUrl) {
        // Update both database and UI state on error
        setPlatformConnectionStatus(prev => ({ ...prev, [platform.id]: 'error' }));
        await updateConnectionStatus(platform.id, 'AUTHENTICATION_FAILED', result.message || 'Failed to initiate Twitter authentication');

        throw new Error(result.message || 'Failed to initiate Twitter authentication');
      }

      // Open the Twitter authorization page in a new window
      window.open(result.authUrl, 'twitter_auth', 'width=600,height=700');

      toast({
        title: 'Twitter Authentication',
        description: 'Please complete the authentication in the popup window',
        duration: 10000,
      });

    } catch (error) {
      console.error('Error initiating Twitter auth:', error);

      // Update both database and UI state on error
      setPlatformConnectionStatus(prev => ({ ...prev, [platform.id]: 'error' }));
      await updateConnectionStatus(platform.id, 'AUTHENTICATION_FAILED', error instanceof Error ? error.message : 'Failed to start Twitter authentication');

      toast({
        title: 'Twitter Authentication Failed',
        description: error instanceof Error ? error.message : 'Failed to start Twitter authentication',
        variant: 'destructive',
      });
    }
  };

  // Handle the callback from X (Twitter) OAuth
  const handleTwitterCallback = async (event: MessageEvent) => {
    // Make sure we only handle messages from our domain
    if (event.origin !== window.location.origin) return;

    // Check if this is our X callback message
    const data = event.data as TwitterCallbackEvent;
    if (data.type !== 'twitter_auth_callback') return;

    try {
      // Remove the message listener
      window.removeEventListener('message', handleTwitterCallback);

      const { code, state, error } = data;

      // Retrieve state and platform info from localStorage
      const storedState = localStorage.getItem('twitter_auth_state');
      const platformId = localStorage.getItem('twitter_auth_platform_id');
      const brandId = localStorage.getItem('twitter_auth_brand_id');

      // Clean up localStorage
      localStorage.removeItem('twitter_auth_state');
      localStorage.removeItem('twitter_auth_platform_id');
      localStorage.removeItem('twitter_auth_brand_id');

      // Verify state to prevent CSRF
      if (state !== storedState) {
        throw new Error('Invalid state parameter. Authentication request may have been tampered with.');
      }

      // Check for errors from the OAuth flow
      if (error) {
        throw new Error(`X (Twitter) authentication error: ${error}`);
      }

      // If we have a valid code, exchange it for an access token
      if (!code) {
        throw new Error('No authorization code received');
      }

      // Call our worker to exchange the code for a token
      const session = await supabase.auth.getSession();
      const accessToken = session.data.session?.access_token;

      const redirectUri = `${window.location.origin}/auth/twitter/callback`;

      const response = await fetch(`${import.meta.env.VITE_WORKERS_URL}/rainfactory/connections/auth-callback/twitter`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          code,
          state,
          redirectUri,
          brandId,
          platformId
        }),
      });

      const result = await response.json();

      if (result.success) {
        // Successfully authenticated with X (Twitter)
        setPlatformConnectionStatus(prev => ({ ...prev, [platformId!]: 'success' }));
        toast({
          title: 'X (Twitter) Connected',
          description: 'Successfully connected to X (Twitter)',
          variant: 'default',
          className: 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-900/50',
        });

        // Update connection status
        await updateConnectionStatus(platformId!, 'CONNECTED');

        // Refresh data to show the updated connection status
        await fetchPlatformSettings(platforms);
      } else {
        throw new Error(result.message || 'Failed to complete X (Twitter) authentication');
      }

    } catch (error) {
      console.error('X (Twitter) callback error:', error);
      toast({
        title: 'X (Twitter) Connection Failed',
        description: error instanceof Error ? error.message : 'Failed to connect to X (Twitter)',
        variant: 'destructive',
      });

      // Reset connection status
      const twitterPlatform = platforms.find(p => p.platform_key === 'twitter');
      if (twitterPlatform) {
        setPlatformConnectionStatus(prev => ({ ...prev, [twitterPlatform.id]: 'error' }));
        setTimeout(() => {
          setPlatformConnectionStatus(prev => ({ ...prev, [twitterPlatform.id]: 'idle' }));
        }, 3000);
      }
    }
  };

  // Handle Instagram callback
  const handleInstagramCallback = async (event: MessageEvent) => {
    // Make sure we only handle messages from our domain
    if (event.origin !== window.location.origin) return;

    // Check if this is our Instagram callback message
    const data = event.data as InstagramCallbackEvent;
    if (data.type !== 'instagram_auth_callback') return;

    try {
      // Remove the message listener
      window.removeEventListener('message', handleInstagramCallback);

      const { code, state, error } = data;

      // Retrieve state and platform info from localStorage
      const storedState = localStorage.getItem('instagram_auth_state');
      const platformId = localStorage.getItem('instagram_auth_platform_id');
      const brandId = localStorage.getItem('instagram_auth_brand_id');

      // Clean up localStorage
      localStorage.removeItem('instagram_auth_state');
      localStorage.removeItem('instagram_auth_platform_id');
      localStorage.removeItem('instagram_auth_brand_id');

      // Verify state to prevent CSRF
      if (state !== storedState) {
        throw new Error('Invalid state parameter. Authentication request may have been tampered with.');
      }

      // Check for errors from the OAuth flow
      if (error) {
        throw new Error(`Instagram authentication error: ${error}`);
      }

      // If we have a valid code, exchange it for an access token
      if (!code) {
        throw new Error('No authorization code received');
      }

      // Call our worker to exchange the code for a token
      const session = await supabase.auth.getSession();
      const accessToken = session.data.session?.access_token;

      if (!accessToken || !platformId || !brandId) {
        throw new Error('Missing required authentication data');
      }

      const response = await fetch(`${import.meta.env.VITE_WORKERS_URL}/rainfactory/auth-callback/instagram`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          code,
          state,
          redirectUri: `${window.location.origin}/auth/instagram/callback`,
          brandId,
          platformId,
        }),
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Failed to complete Instagram authentication');
      }

      // Update UI state
      setPlatformConnectionStatus(prev => ({ ...prev, [platformId]: 'success' }));
      await updateConnectionStatus(platformId, 'CONNECTED');

      toast({
        title: 'Instagram Connected',
        description: 'Successfully connected to Instagram.',
      });

      // Refresh platform settings
      await fetchPlatformSettings(platforms);

    } catch (error) {
      console.error('Error handling Instagram callback:', error);

      // Get platformId from localStorage since we're in the callback
      const storedPlatformId = localStorage.getItem('instagram_auth_platform_id');
      if (storedPlatformId) {
        // Update UI state
        setPlatformConnectionStatus(prev => ({ ...prev, [storedPlatformId]: 'error' }));
        await updateConnectionStatus(storedPlatformId, 'AUTHENTICATION_FAILED', error instanceof Error ? error.message : 'Failed to complete Instagram authentication');
      }

      toast({
        title: 'Instagram Authentication Failed',
        description: error instanceof Error ? error.message : 'Failed to complete Instagram authentication',
        variant: 'destructive',
      });
    }
  };

  // Add Instagram auth flow
  const initiateInstagramAuth = async (platform: Platform, authType?: 'FACEBOOK_LOGIN' | 'INSTAGRAM_LOGIN') => {
    try {
      // Update UI state
      setPlatformConnectionStatus(prev => ({ ...prev, [platform.id]: 'testing' }));
      await updateConnectionStatus(platform.id, 'PENDING');

      // Get access token for API call
      const session = await supabase.auth.getSession();
      const accessToken = session.data.session?.access_token;

      if (!accessToken) {
        throw new Error('No access token available');
      }

      // Call our worker to initiate the OAuth flow
      const response = await fetch(`${import.meta.env.VITE_WORKERS_URL}/rainfactory/connections/initiate-auth/instagram`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          brandId,
          platformId: platform.id,
          redirectUri: `${window.location.origin}/auth/instagram/callback`,
          authType: authType || 'FACEBOOK_LOGIN' // Default to FACEBOOK_LOGIN if not specified
        }),
      });

      const result = await response.json();

      if (!result.success || !result.authUrl) {
        throw new Error(result.message || 'Failed to initiate Instagram authentication');
      }

      // Store state and platform info in localStorage for the callback (use state from backend)
      localStorage.setItem('instagram_auth_state', result.state);
      localStorage.setItem('instagram_auth_platform_id', platform.id);
      localStorage.setItem('instagram_auth_brand_id', brandId || '');

      // Add message listener for the callback
      window.addEventListener('message', handleInstagramCallback);

      // Open popup window
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        'about:blank',
        'instagram_auth_popup',
        `width=${width},height=${height},left=${left},top=${top}`
      );

      if (!popup) {
        throw new Error('Failed to open popup window. Please allow popups for this site.');
      }

      // Navigate popup to auth URL
      popup.location.href = result.authUrl;

    } catch (error) {
      console.error('Error starting Instagram auth:', error);

      // Update UI state
      setPlatformConnectionStatus(prev => ({ ...prev, [platform.id]: 'error' }));
      await updateConnectionStatus(platform.id, 'AUTHENTICATION_FAILED', error instanceof Error ? error.message : 'Failed to start Instagram authentication');

      toast({
        title: 'Instagram Authentication Failed',
        description: error instanceof Error ? error.message : 'Failed to start Instagram authentication',
        variant: 'destructive',
      });
    }
  };

  // Handle Facebook callback
  const handleFacebookCallback = async (event: MessageEvent) => {
    // Make sure we only handle messages from our domain
    if (event.origin !== window.location.origin) return;

    // Check if this is our Facebook callback message
    const data = event.data as FacebookCallbackEvent;
    if (data.type !== 'facebook_auth_callback') return;

    try {
      // Remove the message listener
      window.removeEventListener('message', handleFacebookCallback);

      const { code, state, error } = data;

      // Retrieve state and platform info from localStorage
      const storedState = localStorage.getItem('facebook_auth_state');
      const platformId = localStorage.getItem('facebook_auth_platform_id');
      const brandId = localStorage.getItem('facebook_auth_brand_id');

      // Clean up localStorage
      localStorage.removeItem('facebook_auth_state');
      localStorage.removeItem('facebook_auth_platform_id');
      localStorage.removeItem('facebook_auth_brand_id');

      // Verify state to prevent CSRF
      if (state !== storedState) {
        throw new Error('Invalid state parameter. Authentication request may have been tampered with.');
      }

      // Check for errors from the OAuth flow
      if (error) {
        throw new Error(`Facebook authentication error: ${error}`);
      }

      // If we have a valid code, exchange it for an access token
      if (!code) {
        throw new Error('No authorization code received');
      }

      // Call our worker to exchange the code for a token
      const session = await supabase.auth.getSession();
      const accessToken = session.data.session?.access_token;

      if (!accessToken || !platformId || !brandId) {
        throw new Error('Missing required authentication data');
      }

      const response = await fetch(`${import.meta.env.VITE_WORKERS_URL}/rainfactory/auth-callback/facebook`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          code,
          state,
          redirectUri: `${window.location.origin}/auth/facebook/callback`,
          brandId,
          platformId,
        }),
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Failed to complete Facebook authentication');
      }

      // Update UI state
      setPlatformConnectionStatus(prev => ({ ...prev, [platformId]: 'success' }));
      await updateConnectionStatus(platformId, 'CONNECTED');

      toast({
        title: 'Facebook Connected',
        description: 'Successfully connected to Facebook.',
      });

      // Refresh platform settings
      await fetchPlatformSettings(platforms);

    } catch (error) {
      console.error('Error handling Facebook callback:', error);

      // Get platformId from localStorage since we're in the callback
      const storedPlatformId = localStorage.getItem('facebook_auth_platform_id');
      if (storedPlatformId) {
        // Update UI state
        setPlatformConnectionStatus(prev => ({ ...prev, [storedPlatformId]: 'error' }));
        await updateConnectionStatus(storedPlatformId, 'AUTHENTICATION_FAILED', error instanceof Error ? error.message : 'Failed to complete Facebook authentication');
      }

      toast({
        title: 'Facebook Authentication Failed',
        description: error instanceof Error ? error.message : 'Failed to complete Facebook authentication',
        variant: 'destructive',
      });
    }
  };

  // Add Facebook auth flow
  const initiateFacebookAuth = async (platform: Platform) => {
    try {
      // Update UI state
      setPlatformConnectionStatus(prev => ({ ...prev, [platform.id]: 'testing' }));
      await updateConnectionStatus(platform.id, 'PENDING');

      // Get access token for API call
      const session = await supabase.auth.getSession();
      const accessToken = session.data.session?.access_token;

      if (!accessToken) {
        throw new Error('No access token available');
      }

      // Call our worker to initiate the OAuth flow
      const response = await fetch(`${import.meta.env.VITE_WORKERS_URL}/rainfactory/connections/initiate-auth/facebook`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          brandId,
          platformId: platform.id,
          redirectUri: `${window.location.origin}/auth/facebook/callback`,
        }),
      });

      const result = await response.json();

      if (!result.success || !result.authUrl) {
        throw new Error(result.message || 'Failed to initiate Facebook authentication');
      }

      // Store state and platform info in localStorage for the callback
      localStorage.setItem('facebook_auth_state', result.state);
      localStorage.setItem('facebook_auth_platform_id', platform.id);
      localStorage.setItem('facebook_auth_brand_id', brandId || '');

      // Add message listener for the callback
      window.addEventListener('message', handleFacebookCallback);

      // Open popup window
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        'about:blank',
        'facebook_auth_popup',
        `width=${width},height=${height},left=${left},top=${top}`
      );

      if (!popup) {
        throw new Error('Failed to open popup window. Please allow popups for this site.');
      }

      // Navigate popup to auth URL
      popup.location.href = result.authUrl;

    } catch (error) {
      console.error('Error starting Facebook auth:', error);

      // Update UI state
      setPlatformConnectionStatus(prev => ({ ...prev, [platform.id]: 'error' }));
      await updateConnectionStatus(platform.id, 'AUTHENTICATION_FAILED', error instanceof Error ? error.message : 'Failed to start Facebook authentication');

      toast({
        title: 'Facebook Authentication Failed',
        description: error instanceof Error ? error.message : 'Failed to start Facebook authentication',
        variant: 'destructive',
      });
    }
  };

  // Test platform connection (this would need to be expanded with specific platform logic)
  const testPlatformConnection = async (platformId: string) => {
    if (!brandId) return;

    try {
      setPlatformConnectionStatus(prev => ({ ...prev, [platformId]: 'testing' }));

      // Find the platform
      const platform = platforms.find(p => p.id === platformId);
      if (!platform) {
        throw new Error('Platform not found');
      }

      // Initiate authentication flow for the platform
      await initiateAuthFlow(platform.platform_key);

    } catch (error) {
      setPlatformConnectionStatus(prev => ({ ...prev, [platformId]: 'error' }));
      console.error('Error testing platform connection:', error);

      const errorMessage = error instanceof Error ? error.message : 'An error occurred while testing the connection';

      toast({
        title: 'Connection Error',
        description: errorMessage,
        variant: 'destructive',
      });

      // Update status to failed
      updateConnectionStatus(platformId, 'AUTHENTICATION_FAILED', errorMessage);

      // Reset to idle after delay
      setTimeout(() => {
        setPlatformConnectionStatus(prev => ({ ...prev, [platformId]: 'idle' }));
      }, 3000);
    }
  };

  // New function to fetch LinkedIn organizations
  const fetchLinkedInOrganizations = async (platformId: string) => {
    if (!brandId) return [];

    // Check if we've already attempted to fetch recently (debounce effect)
    const DEBOUNCE_TIME = 10000; // 10 seconds
    const now = Date.now();
    const lastFetchAttempt = parseInt(localStorage.getItem(`linkedin_org_fetch_${platformId}`) || '0');

    if (now - lastFetchAttempt < DEBOUNCE_TIME) {
      console.log('Skipping LinkedIn organizations fetch due to debounce', { now, lastFetchAttempt });
      return linkedinOrganizations; // Return current state
    }

    // Set last fetch attempt timestamp
    localStorage.setItem(`linkedin_org_fetch_${platformId}`, now.toString());

    try {
      setLoadingOrganizations(true);

      // Get current organization settings
      const currentSettings = platformSettings[platformId];

      // Validate the platform settings exist and have a valid status
      if (!currentSettings) {
        console.warn('Platform settings not found for LinkedIn organization fetch');
        return linkedinOrganizations;
      }

      // If platform is not properly connected, don't attempt to fetch organizations
      if (currentSettings.status !== 'CONNECTED') {
        console.log('LinkedIn not connected, skipping organization fetch');
        return linkedinOrganizations;
      }

      const isPostingAsOrg = currentSettings?.connection_details?.post_as_organization === true;
      const currentOrgId = currentSettings?.connection_details?.organization_id;

      // If there's a selected organization but we don't have any organizations loaded yet,
      // create a temporary entry while we wait for the full list to load
      if (isPostingAsOrg && currentOrgId && linkedinOrganizations.length === 0) {
        // Use existing organization_name if available, otherwise use placeholder
        const orgName = currentSettings?.connection_details?.organization_name || `Organization ${currentOrgId}`;
        setLinkedinOrganizations([{
          id: currentOrgId,
          name: orgName
        }]);
      }

      const session = await supabase.auth.getSession();
      const accessToken = session.data.session?.access_token;

      if (!accessToken) {
        throw new Error('No access token available. Please log in again.');
      }

      const response = await fetch(`${import.meta.env.VITE_WORKERS_URL}/rainfactory/connections/list-linkedin-organizations/linkedin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          brandId,
          platformId
        }),
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch LinkedIn organizations');
      }

      const fetchedOrgs = result.organizations || [];
      console.log('Fetched LinkedIn organizations:', fetchedOrgs);

      // Store organizations in state
      setLinkedinOrganizations(fetchedOrgs);

      // If we have a currently selected organization, update its name in connection_details
      if (isPostingAsOrg && currentOrgId) {
        const selectedOrg = fetchedOrgs.find((org: LinkedInOrganization) => org.id === currentOrgId);
        if (selectedOrg && selectedOrg.name) {
          // Update organization name in platform settings
          setPlatformSettings(prev => {
            const updatedSettings = { ...prev };
            if (updatedSettings[platformId] && updatedSettings[platformId].connection_details) {
              updatedSettings[platformId].connection_details = {
                ...updatedSettings[platformId].connection_details,
                organization_name: selectedOrg.name
              };
            }
            return updatedSettings;
          });

          // Also update in database to persist the name
          await supabase
            .from('platform_brand_settings')
            .update({
              connection_details: {
                ...currentSettings.connection_details,
                organization_name: selectedOrg.name
              }
            })
            .eq('id', currentSettings.id);
        }
      }

      return fetchedOrgs;
    } catch (error) {
      console.error('Error fetching LinkedIn organizations:', error);

      // Don't update the connection status every time there's an error
      // Only update if we don't already have organizations and this is a critical error
      if (linkedinOrganizations.length === 0) {
        // const errorMessage = error instanceof Error ? error.message : 'Failed to fetch LinkedIn organizations';

        // Don't change the platform status to AUTHENTICATION_FAILED unless it's a real auth issue
        // This prevents changing DB state on every render
        if (error instanceof Error && error.message.includes('ACCESS_DENIED')) {
          // Silent failure - don't update DB status to avoid infinite loop
          console.warn('LinkedIn API access denied - not updating platform status to avoid loop');

          // Only show toast once
          if (!localStorage.getItem(`linkedin_org_error_shown_${platformId}`)) {
            localStorage.setItem(`linkedin_org_error_shown_${platformId}`, 'true');
            toast({
              title: 'LinkedIn Organization Access',
              description: 'Could not access your LinkedIn organizations. You may need to reconnect your account with proper permissions.',
              variant: 'destructive',
            });
          }
        }
      }

      return linkedinOrganizations; // Return current state instead of empty array
    } finally {
      setLoadingOrganizations(false);
    }
  };

  // New function to set the LinkedIn organization
  const setLinkedInOrganization = async (platformId: string, organizationId: string) => {
    if (!brandId) return;

    // Handle 'personal' value by updating local settings
    if (organizationId === 'personal') {
      try {
        // Get the current settings
        const currentSettings = platformSettings[platformId];
        if (!currentSettings) {
          throw new Error('Platform settings not found');
        }

        // Update with personal account settings
        const updatedConnectionDetails = {
          ...(currentSettings.connection_details || {}),
          organization_id: undefined,
          organization_name: undefined,
          post_as_organization: false
        };

        // Save to the database
        await savePlatformSettings(platformId, {
          connection_details: updatedConnectionDetails
        });

        toast({
          title: 'LinkedIn Profile Set',
          description: 'Posts will be published as your personal LinkedIn profile',
          variant: 'default',
          className: 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-900/50',
        });

        return true;
      } catch (error) {
        console.error('Error setting LinkedIn to personal account:', error);
        toast({
          title: 'Error',
          description: error instanceof Error ? error.message : 'Failed to update LinkedIn settings',
          variant: 'destructive',
        });
        return false;
      }
    }

    // Standard organization selection flow
    try {
      // Find organization name from the organizations list
      const selectedOrg = linkedinOrganizations.find(org => org.id === organizationId);
      const orgName = selectedOrg?.name || `Organization ${organizationId}`;

      const session = await supabase.auth.getSession();
      const accessToken = session.data.session?.access_token;

      if (!accessToken) {
        throw new Error('No access token available. Please log in again.');
      }

      const response = await fetch(`${import.meta.env.VITE_WORKERS_URL}/rainfactory/connections/set-linkedin-organization/linkedin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          brandId,
          platformId,
          organizationId
        }),
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Failed to set LinkedIn organization');
      }

      // Update local state with the new organization selection
      setPlatformSettings(prev => {
        const updatedSettings = { ...prev };
        if (updatedSettings[platformId]) {
          const connectionDetails = updatedSettings[platformId].connection_details || {};

          updatedSettings[platformId] = {
            ...updatedSettings[platformId],
            connection_details: {
              ...connectionDetails,
              organization_id: organizationId,
              organization_name: orgName, // Explicitly store name
              post_as_organization: true
            }
          };
        }
        return updatedSettings;
      });

      toast({
        title: 'LinkedIn Organization Set',
        description: 'LinkedIn organization has been successfully set',
        variant: 'default',
        className: 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-900/50',
      });

      // Refresh data to show the updated settings
      await fetchPlatformSettings(platforms);

      return true;
    } catch (error) {
      console.error('Error setting LinkedIn organization:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to set LinkedIn organization',
        variant: 'destructive',
      });
      return false;
    }
  };

  // New function to set the Facebook page
  const setFacebookPage = async (platformId: string, pageId: string) => {
    console.log('=== setFacebookPage CALLED ===');
    console.log('platformId:', platformId);
    console.log('pageId:', pageId);
    console.log('brandId:', brandId);

    if (!brandId) return;

    // Handle 'personal' value by updating local settings
    if (pageId === 'personal') {
      try {
        // Get the current settings
        const currentSettings = platformSettings[platformId];
        if (!currentSettings) {
          throw new Error('Platform settings not found');
        }

        // Update with personal account settings
        const updatedConnectionDetails = {
          ...(currentSettings.connection_details || {}),
          selected_page_id: undefined,
          selected_page_name: undefined
        };

        // Save to the database
        await savePlatformSettings(platformId, {
          connection_details: updatedConnectionDetails
        });

        toast({
          title: 'Facebook Profile Set',
          description: 'Posts will be published as your personal Facebook profile',
          variant: 'default',
          className: 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-900/50',
        });

        return true;
      } catch (error) {
        console.error('Error setting Facebook to personal account:', error);
        toast({
          title: 'Error',
          description: error instanceof Error ? error.message : 'Failed to update Facebook settings',
          variant: 'destructive',
        });
        return false;
      }
    }

    // Standard page selection flow
    try {
      // Find page name from the pages list before making the API call
      const currentSettings = platformSettings[platformId];
      console.log('Current settings:', currentSettings);

      const facebookPages = (currentSettings?.connection_details as any)?.pages || [];
      console.log('Facebook pages available:', facebookPages);

      const selectedPage = facebookPages.find((page: any) => page.id === pageId);
      console.log('Selected page object:', selectedPage);

      const pageName = selectedPage?.name || `Page ${pageId}`;
      console.log('Page name to be set:', pageName);

      const session = await supabase.auth.getSession();
      const accessToken = session.data.session?.access_token;

      if (!accessToken) {
        throw new Error('No access token available. Please log in again.');
      }

      console.log('Making API call to set Facebook page...');
      const response = await fetch(`${import.meta.env.VITE_WORKERS_URL}/rainfactory/connections/set-facebook-page/facebook`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          brandId,
          platformId,
          pageId
        }),
      });

      const result = await response.json();
      console.log('API response:', result);

      if (!result.success) {
        throw new Error(result.message || 'Failed to set Facebook page');
      }

      console.log('Updating local state with page ID:', pageId, 'and page name:', pageName);

      // Update local state immediately with the new page selection
      setPlatformSettings(prev => {
        const updatedSettings = { ...prev };
        if (updatedSettings[platformId]) {
          const connectionDetails = updatedSettings[platformId].connection_details || {};

          updatedSettings[platformId] = {
            ...updatedSettings[platformId],
            connection_details: {
              ...connectionDetails,
              selected_page_id: pageId,
              selected_page_name: pageName
            }
          };

          console.log('Updated platform settings:', updatedSettings[platformId]);
        }
        return updatedSettings;
      });

      toast({
        title: 'Facebook Page Set',
        description: 'Facebook page has been successfully set',
        variant: 'default',
        className: 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-900/50',
      });

      console.log('Refreshing platform settings from database...');
      // Refresh data to show the updated settings
      await fetchPlatformSettings(platforms);
      console.log('=== setFacebookPage COMPLETE ===');

      return true;
    } catch (error) {
      console.error('Error setting Facebook page:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to set Facebook page',
        variant: 'destructive',
      });
      return false;
    }
  };

  // Return hook values
  return {
    platforms,
    platformSettings,
    platformConnectionStatus,
    loading,
    savePlatformSettings,
    updateConnectionStatus,
    testPlatformConnection,
    initiateAuthFlow,
    // LinkedIn organizations functionality
    fetchLinkedInOrganizations,
    setLinkedInOrganization,
    linkedinOrganizations,
    loadingOrganizations,
    // Facebook page functionality
    setFacebookPage
  };
};

export default usePlatformSettings;